import { useState } from "react";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";

import { RoleLink } from "@/components/role-link";
import { ChangeCancelDialog } from "./change-cancel-dialog";
import { useOpenShift } from "@/components/shift-detail/use-shift-modal";
import { captureEvent } from "@/lib/analytics/posthog";
import type { WorkerUpcomingShift } from "@/queries/api/workerDashboardApi";
import {
  useConfirmShiftMutation,
  useWithdrawShiftMutation,
  useWorkerUpcomingQuery,
} from "@/queries/hooks/workerDashboardHooks";
import { cn } from "@/utils";
import { useIsMobile } from "@/utils/hooks/use-is-mobile";
import { useOfferResponse } from "@/utils/hooks/use-offer-response";

function parse(iso?: string | null): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

function sameDay(a: Date, b: Date) {
  return a.toDateString() === b.toDateString();
}

function directionsUrl(r: WorkerUpcomingShift["restaurant"]): string | null {
  if (!r) return null;
  if (r.lat != null && r.lng != null)
    return `https://www.google.com/maps/dir/?api=1&destination=${r.lat},${r.lng}`;
  if (r.address)
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(r.address)}`;
  return null;
}

/** Desktop upcoming-shift card; also used for the worker "My shifts" page. */
export function UpcomingShiftCard({ shift }: { shift: WorkerUpcomingShift }) {
  const { t } = useTranslation("dashboard");
  const openShift = useOpenShift();
  const confirm = useConfirmShiftMutation();
  const withdraw = useWithdrawShiftMutation();
  const offer = useOfferResponse("upcoming");
  const [changeCancelOpen, setChangeCancelOpen] = useState(false);

  const start = parse(shift.start_at);
  const end = parse(shift.end_at);
  const checkin = parse(shift.checkin_opens_at);
  const status = shift.status;
  const applied = status === "applied";
  const offered = status === "offered";
  const confirmed = status === "confirmed";

  const pillCls =
    applied || offered ? "bg-warn-soft text-warn-deep" : "bg-teal-soft text-teal-deep";

  let dayLabel = "";
  if (start) {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    const prefix = sameDay(start, today)
      ? `${t("workerV2.upcoming.today")} · `
      : sameDay(start, tomorrow)
        ? `${t("workerV2.upcoming.tomorrow")} · `
        : "";
    dayLabel = prefix + format(start, "EEE MMM d").toUpperCase();
  }

  const timeLine = [
    start && end ? `${format(start, "h:mma").toLowerCase()}–${format(end, "h:mma").toLowerCase()}` : null,
    shift.rate != null ? `$${shift.rate}/hr` : null,
    shift.estimated_pay != null ? `~$${shift.estimated_pay}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const note = offered
    ? t("workerV2.upcoming.note.offered", "First to accept gets the shift")
    : applied
    ? t("workerV2.upcoming.note.awaiting")
    : confirmed
      ? checkin
        ? t("workerV2.upcoming.note.checkinOpens", { time: format(checkin, "h:mma").toLowerCase() })
        : ""
      : t("workerV2.upcoming.note.confirmToLock");

  const dirUrl = directionsUrl(shift.restaurant);
  const confirming = confirm.isPending && String(confirm.variables) === String(shift.shift_id);

  const onWithdraw = () => {
    if (window.confirm(t("workerV2.upcoming.withdrawConfirm"))) {
      captureEvent("upcoming_action", { status, action: "withdraw" });
      withdraw.mutate(shift.shift_id);
    }
  };

  const addressLine = [
    shift.restaurant?.address ?? "",
    shift.distance_mi != null ? t("workerV2.upcoming.away", { mi: shift.distance_mi }) : "",
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div
      className={cn(
        "flex flex-col gap-2 rounded-[15px] bg-white border p-4",
        applied || offered ? "border-warn-border" : "border-fog-dark",
      )}
    >
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold tracking-wide text-slate">{dayLabel}</span>
        <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-semibold", pillCls)}>
          {t(`workerV2.upcoming.status.${status}`)}
        </span>
      </div>

      <div className="text-[15px] font-bold text-garnet">
        {shift.role ?? ""}
        {shift.restaurant?.name ? <span className="font-semibold"> · {shift.restaurant.name}</span> : null}
      </div>

      {timeLine ? <div className="text-sm text-garnet/80">{timeLine}</div> : null}
      {addressLine ? <div className="text-xs text-slate">{addressLine}</div> : null}
      {note ? <div className="text-xs text-slate">{note}</div> : null}

      <div className="mt-1 flex flex-wrap gap-2">
        {dirUrl ? (
          <a
            href={dirUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => captureEvent("upcoming_action", { status, action: "directions" })}
            className="h-9 px-3 grid place-items-center rounded-lg border border-fog-dark text-xs font-semibold text-garnet hover:bg-fog no-underline!"
          >
            {t("workerV2.upcoming.action.directions")}
          </a>
        ) : null}

        {status === "approved" ? (
          <button
            type="button"
            disabled={confirming}
            onClick={() => {
              captureEvent("upcoming_action", { status, action: "confirm" });
              confirm.mutate(shift.shift_id);
            }}
            className="h-9 px-3 rounded-lg bg-garnet text-white text-xs font-semibold disabled:opacity-60"
          >
            {confirming ? t("workerV2.forYou.confirming") : t("workerV2.upcoming.action.confirm")}
          </button>
        ) : null}

        {offered ? (
          <>
            <button
              type="button"
              disabled={offer.isBusy(shift.shift_id)}
              onClick={() => offer.accept(shift.shift_id)}
              className="h-9 px-3 rounded-lg bg-garnet text-white text-xs font-semibold disabled:opacity-60"
            >
              {t("workerV2.offer.accept", "Accept")}
            </button>
            <button
              type="button"
              disabled={offer.isBusy(shift.shift_id)}
              onClick={() => offer.decline(shift.shift_id)}
              className="h-9 px-3 rounded-lg border border-fog-dark text-xs font-semibold text-garnet hover:bg-fog disabled:opacity-60"
            >
              {t("workerV2.offer.decline", "Decline")}
            </button>
          </>
        ) : applied ? (
          <button
            type="button"
            disabled={withdraw.isPending}
            onClick={onWithdraw}
            className="h-9 px-3 rounded-lg border border-warn-border text-xs font-semibold text-warn-deep hover:bg-warn-soft/40 disabled:opacity-60"
          >
            {t("workerV2.upcoming.action.withdraw")}
          </button>
        ) : (
          <>
            <button
              type="button"
              onClick={() => {
                captureEvent("upcoming_action", { status, action: "details" });
                openShift(shift.shift_id);
              }}
              className="h-9 px-3 rounded-lg border border-fog-dark text-xs font-semibold text-garnet hover:bg-fog"
            >
              {t("workerV2.upcoming.action.details")}
            </button>
            <button
              type="button"
              onClick={() => {
                captureEvent("upcoming_action", { status, action: "change_cancel" });
                setChangeCancelOpen(true);
              }}
              className="h-9 px-3 rounded-lg border border-warn-border text-xs font-semibold text-warn-deep hover:bg-warn-soft/40"
            >
              {t("workerV2.upcoming.action.changeCancel")}
            </button>
          </>
        )}
      </div>

      <ChangeCancelDialog
        shift={shift}
        open={changeCancelOpen}
        onOpenChange={setChangeCancelOpen}
      />
    </div>
  );
}

function dayLabelFor(start: Date | null, t: ReturnType<typeof useTranslation>["t"]): string {
  if (!start) return "";
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  const prefix = sameDay(start, today)
    ? `${t("workerV2.upcoming.today")} · `
    : sameDay(start, tomorrow)
      ? `${t("workerV2.upcoming.tomorrow")} · `
      : "";
  return prefix + format(start, "EEE MMM d").toUpperCase();
}

/** Phone "Next up" hero (SPEC §3.5) — dark card for the soonest shift. */
function HeroCard({ shift }: { shift: WorkerUpcomingShift }) {
  const { t } = useTranslation("dashboard");
  const openShift = useOpenShift();
  const confirm = useConfirmShiftMutation();
  const offer = useOfferResponse("upcoming");
  const [changeCancelOpen, setChangeCancelOpen] = useState(false);
  const start = parse(shift.start_at);
  const end = parse(shift.end_at);
  const confirming = confirm.isPending && String(confirm.variables) === String(shift.shift_id);
  const dir = directionsUrl(shift.restaurant);

  const timeLine = [
    start && end ? `${format(start, "h:mma").toLowerCase()}–${format(end, "h:mma").toLowerCase()}` : null,
    shift.rate != null ? `$${shift.rate}/hr` : null,
    shift.estimated_pay != null ? `~$${shift.estimated_pay}` : null,
  ]
    .filter(Boolean)
    .join(" · ");
  const addr = [
    shift.restaurant?.address ?? "",
    shift.distance_mi != null ? t("workerV2.upcoming.away", { mi: shift.distance_mi }) : "",
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="rounded-[15px] bg-garnet text-white p-4 flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold tracking-wide text-white/80">
          {dayLabelFor(start, t)}
        </span>
        <span className="rounded-full px-2 py-0.5 text-[11px] font-semibold bg-white/15">
          {t(`workerV2.upcoming.status.${shift.status}`)}
        </span>
      </div>
      <div className="text-[17px] font-bold">
        {shift.role ?? ""}
        {shift.restaurant?.name ? ` · ${shift.restaurant.name}` : ""}
      </div>
      {timeLine ? <div className="text-sm text-white/85">{timeLine}</div> : null}
      {addr ? <div className="text-xs text-white/70">{addr}</div> : null}
      <div className="mt-2 flex gap-2">
        {shift.status === "offered" ? (
          <button
            type="button"
            disabled={offer.isBusy(shift.shift_id)}
            onClick={() => offer.accept(shift.shift_id)}
            className="flex-1 min-h-[44px] rounded-lg bg-white text-garnet text-sm font-bold disabled:opacity-60"
          >
            {t("workerV2.offer.accept", "Accept")}
          </button>
        ) : shift.status === "approved" ? (
          <button
            type="button"
            disabled={confirming}
            onClick={() => {
              captureEvent("upcoming_action", { status: shift.status, action: "confirm" });
              confirm.mutate(shift.shift_id);
            }}
            className="flex-1 min-h-[44px] rounded-lg bg-white text-garnet text-sm font-bold disabled:opacity-60"
          >
            {confirming
              ? t("workerV2.forYou.confirming")
              : t("workerV2.upcoming.action.confirmImComing")}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => {
              captureEvent("upcoming_action", { status: shift.status, action: "details" });
              openShift(shift.shift_id);
            }}
            className="flex-1 min-h-[44px] rounded-lg bg-white/15 text-white text-sm font-bold"
          >
            {t("workerV2.upcoming.action.details")}
          </button>
        )}
        {dir ? (
          <a
            href={dir}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => captureEvent("upcoming_action", { status: shift.status, action: "directions" })}
            className="min-h-[44px] px-4 grid place-items-center rounded-lg border border-white/40 text-white! text-sm font-semibold no-underline!"
          >
            {t("workerV2.upcoming.action.directions")}
          </a>
        ) : null}
      </div>
      {shift.status === "offered" ? (
        <button
          type="button"
          disabled={offer.isBusy(shift.shift_id)}
          onClick={() => offer.decline(shift.shift_id)}
          className="self-start text-xs font-semibold text-white/80 underline underline-offset-2"
        >
          {t("workerV2.offer.decline", "Decline")}
        </button>
      ) : shift.status !== "applied" ? (
        <button
          type="button"
          onClick={() => {
            captureEvent("upcoming_action", {
              status: shift.status,
              action: "change_cancel",
            });
            setChangeCancelOpen(true);
          }}
          className="self-start text-xs font-semibold text-white/80 underline underline-offset-2"
        >
          {t("workerV2.upcoming.action.changeCancel")}
        </button>
      ) : null}

      <ChangeCancelDialog
        shift={shift}
        open={changeCancelOpen}
        onOpenChange={setChangeCancelOpen}
      />
    </div>
  );
}

/** Phone compact card for the horizontal scroller of other upcoming shifts (SPEC §3.5). */
function MiniCard({ shift }: { shift: WorkerUpcomingShift }) {
  const { t } = useTranslation("dashboard");
  const openShift = useOpenShift();
  const start = parse(shift.start_at);
  const applied = shift.status === "applied" || shift.status === "offered";
  return (
    <button
      type="button"
      onClick={() => openShift(shift.shift_id)}
      className={cn(
        "shrink-0 w-44 text-left rounded-[15px] bg-white border p-3 flex flex-col gap-1",
        applied ? "border-warn-border" : "border-fog-dark",
      )}
    >
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-semibold tracking-wide text-slate">{dayLabelFor(start, t)}</span>
        <span
          className={cn(
            "rounded-full px-1.5 text-[10px] font-semibold",
            applied ? "bg-warn-soft text-warn-deep" : "bg-teal-soft text-teal-deep",
          )}
        >
          {t(`workerV2.upcoming.status.${shift.status}`)}
        </span>
      </div>
      <div className="text-sm font-bold text-garnet truncate">
        {shift.role ?? ""}
        {shift.restaurant?.name ? ` · ${shift.restaurant.name}` : ""}
      </div>
      <div className="text-xs text-slate">
        {start ? format(start, "h:mma").toLowerCase() : ""}
        {shift.estimated_pay != null ? ` · ~$${shift.estimated_pay}` : ""}
      </div>
    </button>
  );
}

/** The dashboard shows the soonest few; the rest live on My Shifts (schedule) (linked as "+N more"). */
const DASHBOARD_LIMIT = 3;

/** "Your upcoming shifts" (SPEC handoff-worker §3.2 desktop / §3.5 phone). */
export function UpcomingShifts() {
  const { t } = useTranslation("dashboard");
  const isMobile = useIsMobile();
  const { data, isLoading } = useWorkerUpcomingQuery(DASHBOARD_LIMIT);
  const shifts = data?.items;
  const more = data ? Math.max(0, data.total - data.items.length) : 0;
  const moreLink =
    more > 0 ? (
      <RoleLink to="/app/$role/schedule" className="self-start text-sm font-semibold text-teal no-underline!">
        {t("workerV2.upcoming.moreCount", { count: more })}
      </RoleLink>
    ) : null;

  if (isMobile && shifts && shifts.length > 0) {
    const [hero, ...rest] = shifts;
    return (
      <section className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h2 className="text-[18px] font-semibold text-garnet">{t("workerV2.upcoming.title")}</h2>
          <RoleLink to="/app/$role/schedule" className="text-sm text-teal no-underline!">
            {t("workerV2.upcoming.viewAll")}
          </RoleLink>
        </div>
        <HeroCard shift={hero} />
        {rest.length > 0 ? (
          <div className="flex gap-3 overflow-x-auto pb-1 -mx-1 px-1">
            {rest.map((s) => (
              <MiniCard key={s.shift_id} shift={s} />
            ))}
          </div>
        ) : null}
        {moreLink}
      </section>
    );
  }

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h2 className="text-[18px] font-semibold text-garnet">{t("workerV2.upcoming.title")}</h2>
        <RoleLink to="/app/$role/schedule" className="text-sm text-teal no-underline!">
          {t("workerV2.upcoming.viewAll")}
        </RoleLink>
      </div>

      {isLoading ? (
        <div className="grid gap-3 md:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-40 rounded-[15px] bg-white border border-fog-dark animate-pulse" />
          ))}
        </div>
      ) : !shifts || shifts.length === 0 ? (
        <p className="text-sm text-slate py-4">{t("workerV2.upcoming.empty")}</p>
      ) : (
        <div className="grid gap-3 md:grid-cols-3">
          {shifts.map((s) => (
            <UpcomingShiftCard key={s.shift_id} shift={s} />
          ))}
        </div>
      )}
      {moreLink}
    </section>
  );
}
